import type { FormEvent } from "react";
import { useState } from "react";
import { boatSearchSchema } from "../../../../common/src";
import { navigate } from "../../hooks/useRoute";
import type { TripParams } from "../../routes";
import { toFieldErrors } from "../../utils/fieldErrors";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { useFormStyles } from "../shared/styles";

const toNumber = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

/** Search form; applying it updates the URL, which drives the results. */
interface SearchFiltersProps {
  trip: TripParams;
  canReset: boolean;
}

export const SearchFilters = ({ trip, canReset }: SearchFiltersProps) => {
  const form = useFormStyles();
  const [partySize, setPartySize] = useState(trip.partySize?.toString() ?? "");
  const [startDate, setStartDate] = useState(trip.startDate ?? "");
  const [endDate, setEndDate] = useState(trip.endDate ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = boatSearchSchema.safeParse({
      filters: { partySize: toNumber(partySize) },
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    if (!parsed.success) {
      const fieldErrors = toFieldErrors(parsed.error);
      // Filter errors are nested under "filters"; surface the party size message directly.
      const partyIssue = parsed.error.issues.find(
        (i) => i.path[1] === "partySize"
      );
      setErrors({ ...fieldErrors, partySize: partyIssue?.message ?? "" });
      return;
    }
    setErrors({});
    navigate({
      page: "find",
      trip: {
        partySize: parsed.data.filters.partySize,
        startDate: parsed.data.startDate,
        endDate: parsed.data.endDate,
        // Blank dates mean "show every boat", not "use the default dates".
        browse: parsed.data.startDate === undefined ? true : undefined,
      },
    });
  };

  return (
    <Card title="Find a boat">
      <form onSubmit={submit} noValidate>
        <div className={form.grid}>
          <Field
            label="People"
            htmlFor="find-party"
            hint="Including you"
            error={errors.partySize || undefined}
          >
            <input
              id="find-party"
              type="number"
              min={1}
              className={form.control}
              value={partySize}
              placeholder="Any"
              onChange={(e) => setPartySize(e.target.value)}
            />
          </Field>
          <Field
            label="First day"
            htmlFor="find-start"
            hint="Leave blank to see all boats"
            error={errors.startDate}
          >
            <input
              id="find-start"
              type="date"
              className={form.control}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field
            label="Last day"
            htmlFor="find-end"
            hint="Optional"
            error={errors.endDate}
          >
            <input
              id="find-end"
              type="date"
              className={form.control}
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
        </div>
        <div className={form.actions}>
          <Button type="submit">Search</Button>
          {canReset && (
            <Button
              variant="ghost"
              onClick={() => navigate({ page: "find", trip: {} })}
            >
              Reset
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};
